import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Megaphone, AlertTriangle, AlertOctagon, Info } from "lucide-react";

const PRIORITY = {
  info: { label: "Informativo", color: "#52525B", Icon: Info },
  important: { label: "Importante", color: "#D97706", Icon: AlertTriangle },
  urgent: { label: "Urgente", color: "#E63946", Icon: AlertOctagon },
};

const AUDIENCE = {
  all: "Toda la organización",
  drivers: "Conductores",
  staff: "Personal interno",
};

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/announcements");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-5xl mx-auto" data-testid="announcements-page">
      <header className="flex items-end justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Centro de Control
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight flex items-center gap-3">
            <Megaphone className="sm:w-9 sm:h-9" /> Comunicados
          </h1>
        </div>
        {user?.role === "admin" && (
          <Button
            onClick={() => navigate("/announcements/new")}
            className="rounded-none btn-primary h-11 px-4 sm:px-5 text-xs uppercase tracking-[0.2em]"
            data-testid="new-announcement-cta"
          >
            <Plus size={16} className="sm:mr-2" />
            <span className="hidden sm:inline">Nuevo comunicado</span>
          </Button>
        )}
      </header>

      {loading ? (
        <div className="text-center text-zinc-500 py-12">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="border border-black p-10 text-center text-zinc-500" data-testid="announcements-empty">
          Aún no hay comunicados.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => {
            const p = PRIORITY[a.priority] || PRIORITY.info;
            const Icon = p.Icon;
            return (
              <li key={a.id}>
                <Link
                  to={`/announcements/${a.id}`}
                  data-testid={`announcement-${a.id}`}
                  className={`block border border-black p-4 sm:p-5 bg-white hover:bg-zinc-50 transition-colors ${
                    !a.is_read ? "border-l-[6px]" : ""
                  }`}
                  style={{ borderLeftColor: !a.is_read ? p.color : undefined }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                        style={{ borderColor: p.color, color: p.color }}
                      >
                        <Icon size={11} /> {p.label}
                      </span>
                      {!a.is_read && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-black">
                          • Nuevo
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {new Date(a.created_at).toLocaleString("es-ES")}
                    </span>
                  </div>
                  <div className="font-display text-lg sm:text-xl font-bold mb-1">
                    {a.title}
                  </div>
                  <p className="text-sm text-zinc-600 line-clamp-2">{a.body}</p>
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500">
                    {a.created_by_name}
                    {a.created_by_department ? ` · ${a.created_by_department}` : ""}{" "}
                    · Para: {AUDIENCE[a.audience]}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
